import { Container, Text } from 'pixi.js';
import type { FrameStats } from '../core/GameLoop';
import { Palette } from '../render/Palette';

const RING = 180;

/**
 * On-screen perf HUD: fps, avg/p95/worst frame ms, sim/render split, plus
 * whatever the active scene reports via `extra`. Acceptance criteria for the
 * P1 bullet gate are measured here, not with the browser's coarse FPS meter.
 */
export class PerfOverlay {
  readonly root = new Container();
  /** Scenes set this to report bullet counts etc. */
  extra: (() => string) | null = null;

  private text: Text;
  private frameMs = new Float32Array(RING);
  private cpuMs = new Float32Array(RING);
  private simMs = new Float32Array(RING);
  private renderMs = new Float32Array(RING);
  private idx = 0;
  private filled = 0;
  private sinceRefresh = 0;

  constructor(visible: boolean) {
    this.text = new Text({
      text: '',
      style: {
        fontFamily: 'monospace',
        fontSize: 12,
        fill: Palette.hudText,
        lineHeight: 16,
      },
    });
    this.text.position.set(8, 8);
    this.root.addChild(this.text);
    this.root.visible = visible;
  }

  record(stats: FrameStats): void {
    this.frameMs[this.idx] = stats.frameMs;
    this.cpuMs[this.idx] = stats.cpuMs;
    this.simMs[this.idx] = stats.simMs;
    this.renderMs[this.idx] = stats.renderMs;
    this.idx = (this.idx + 1) % RING;
    if (this.filled < RING) this.filled++;

    if (++this.sinceRefresh >= 15) {
      this.sinceRefresh = 0;
      this.refresh();
    }
  }

  private refresh(): void {
    if (!this.root.visible || this.filled === 0) return;
    const n = this.filled;
    const frames: number[] = [];
    let cpuSum = 0;
    let simSum = 0;
    let renderSum = 0;
    for (let i = 0; i < n; i++) {
      frames.push(this.frameMs[i] ?? 0);
      cpuSum += this.cpuMs[i] ?? 0;
      simSum += this.simMs[i] ?? 0;
      renderSum += this.renderMs[i] ?? 0;
    }
    frames.sort((a, b) => a - b);
    const avg = frames.reduce((a, b) => a + b, 0) / n;
    const p95 = frames[Math.min(n - 1, Math.floor(n * 0.95))] ?? 0;
    const worst = frames[n - 1] ?? 0;
    const fps = avg > 0 ? 1000 / avg : 0;

    let out =
      `fps ${fps.toFixed(0)}  frame avg ${avg.toFixed(2)}ms  p95 ${p95.toFixed(2)}ms  worst ${worst.toFixed(2)}ms\n` +
      `cpu ${(cpuSum / n).toFixed(2)}ms (sim ${(simSum / n).toFixed(2)} + render ${(renderSum / n).toFixed(2)})`;
    if (this.extra) out += `\n${this.extra()}`;
    this.text.text = out;
  }
}
